import { lazy, Suspense, useEffect, useMemo } from 'react';
import { BootController } from '@/shell/Boot/BootController';
import { Desktop } from '@/shell/Desktop/Desktop';

import { useIsMobile } from '@/mobile/useIsMobile';
import { installBackButton } from '@/router/backButton';
import { hasFlag, parseDeepLink } from '@/router/deepLink';
import { useDeepLinkSync } from '@/router/useDeepLinkSync';
import { installUnlockListener } from '@/lib/sound';
import { useWindowStore } from '@/wm/store';

/**
 * Module scope, not a ref: `DesktopRoot` unmounts whenever the viewport
 * crosses the mobile breakpoint, and a ref would reset with it — rotating a
 * tablet twice would then re-open the deep-linked window each time. This is
 * per-document, which is exactly the lifetime the URL has.
 */
let deepLinkOpened = false;

/**
 * Lazy, and worth the round trip: the iPod shell runs the same mixer Media
 * Player and Winamp do, and importing it here eagerly put the whole audio
 * engine into the entry chunk — five kilobytes gzipped that no desktop
 * visitor will ever execute. Phones pay one extra request for a shell they
 * were always going to download.
 */
const IpodShell = lazy(() => import('@/ipod/IpodShell').then((m) => ({ default: m.IpodShell })));

function DesktopRoot({
  deepLinkAppId,
  deepLinkArgs,
  skipBoot,
}: {
  deepLinkAppId: string | null;
  deepLinkArgs: Record<string, unknown>;
  skipBoot: boolean;
}) {
  useDeepLinkSync();

  // The Back button, which Internet Explorer borrows while it is the focused
  // window and has a page to go back to. Installed beside the address-bar sync
  // because the two share the history stack and have to agree about it.
  useEffect(() => installBackButton(), []);

  // Also covers StrictMode's double effect invocation in dev, which would
  // otherwise open the deep-linked window twice.
  useEffect(() => {
    if (!deepLinkAppId || deepLinkOpened) return;
    deepLinkOpened = true;
    useWindowStore.getState().open(deepLinkAppId, deepLinkArgs);
  }, [deepLinkAppId, deepLinkArgs]);

  return (
    <BootController skip={skipBoot}>
      <Desktop />
    </BootController>
  );
}

export function Root() {
  const isMobile = useIsMobile();

  // Parsed once: the URL is read at load, and useDeepLinkSync rewrites it
  // afterwards, so re-parsing later would re-open windows.
  const deepLink = useMemo(() => parseDeepLink(), []);
  const noboot = useMemo(() => hasFlag('noboot'), []);

  useEffect(() => installUnlockListener(), []);

  // No fallback: the panel is unlit until the first touch anyway, and a
  // spinner in front of a device that boots instantly reads as a fault.
  if (isMobile)
    return (
      <Suspense fallback={null}>
        <IpodShell />
      </Suspense>
    );

  return (
    <DesktopRoot
      deepLinkAppId={deepLink?.appId ?? null}
      deepLinkArgs={deepLink?.args ?? {}}
      skipBoot={Boolean(deepLink) || noboot}
    />
  );
}
